import { LockOutlined, WarningOutlined } from '@ant-design/icons';
import { Card, Col, Form, Input, InputNumber, Row, Tag, Tooltip, Typography } from 'antd';
import React from 'react';
import {
  currencyFormatter,
  currencyFormatterNoDecimal,
  currencyParser,
} from './currency';

// Building blocks of the note form: one Card per section, a 3-column grid, the typed inputs (percent,
// months, currency, info text) and the two label marks the operator reads before typing — SENSITIVE
// (the backend guards it or Alto depends on it) and SYSTEM (owned by a scheduler or an integration; the
// input is shown disabled and the value is not the operator's to change). Shared by the Add Property
// wizard and the note detail.

export const NOTE_TYPES = {
  individual: 'individual_note',
  rolling: 'rolling_note',
  constitution: 'constitution_note',
  creditFund: 'credit_fund',
  demand: 'demand_note',
};

export const NOTE_TYPE_LABELS = {
  individual_note: 'Individual note',
  rolling_note: 'Rolling Note',
  constitution_note: 'Constitution Note',
  credit_fund: 'Credit Fund',
  demand_note: 'Demand Note',
};

// The repayment vocabulary the backend validates (investment.schema enums): a free-typed value is
// refused with INVESTMENT_INVALID_VALUE naming the select, so both surfaces offer the exact words.
export const REPAYMENT_TYPES = [
  'Interest Only',
  'Principal and Interest',
  'Distributions',
  'Demand Note',
];

// antd Upload -> Form.Item bridge (valuePropName="fileList").
export const fileListFromEvent = (e) => (Array.isArray(e) ? e : e && e.fileList);

export const COL_3 = { xs: 24, md: 12, xl: 8 };
export const COL_2 = { xs: 24, md: 12 };
export const COL_FULL = { xs: 24 };

const { TextArea } = Input;
const { Text } = Typography;

// The label of a box as the TL settled it (2026-09-10): the word the investor reads on the dashboard, and
// UNDER it one line saying what the column does. The line belongs to the LABEL, not to `extra`: `extra`
// hangs off the value (the stored image, the uploaded files, the last yield change). The wrapping is the
// Form's `labelWrap` — antd clips a multi-line label without it.
export function BoxLabel({ children, hint }) {
  return (
    <span style={{ display: 'block', lineHeight: 1.35 }}>
      {children}
      <Text
        type="secondary"
        style={{ display: 'block', fontWeight: 'normal', fontSize: 12, whiteSpace: 'normal' }}
      >
        {hint}
      </Text>
    </span>
  );
}

export function monthsInput(name, placeholder, disabled) {
  return (
    <Input
      name={name}
      placeholder={placeholder}
      addonAfter="months"
      type="number"
      disabled={disabled}
    />
  );
}

export function currencyInput(noDecimal, disabled) {
  return (
    <InputNumber
      formatter={noDecimal ? currencyFormatterNoDecimal : currencyFormatter}
      parser={currencyParser}
      style={{ width: '100%' }}
      disabled={disabled}
    />
  );
}

export function infoInput(name, placeholder) {
  return (
    <TextArea name={name} placeholder={placeholder} autoSize={{ minRows: 1, maxRows: 4 }} />
  );
}

export function SensitiveLabel({ text, why }) {
  return (
    <span>
      {text}
      <Tooltip title={why}>
        <Tag
          color="orange"
          icon={<WarningOutlined />}
          style={{ marginLeft: 8, fontSize: 11, lineHeight: '18px' }}
        >
          sensitive
        </Tag>
      </Tooltip>
    </span>
  );
}

export function SystemLabel({ text, why }) {
  return (
    <span>
      {text}
      <Tooltip title={why}>
        <Tag
          icon={<LockOutlined />}
          style={{ marginLeft: 8, fontSize: 11, lineHeight: '18px' }}
        >
          system
        </Tag>
      </Tooltip>
    </span>
  );
}

export function Section({ title, hint, children, ...card }) {
  return (
    <Card
      size="small"
      title={title}
      extra={hint}
      style={{ marginBottom: 16 }}
      {...card}
    >
      <Row gutter={16}>{children}</Row>
    </Card>
  );
}

// One field of the grid: a Col around a Form.Item. `span` picks the column width (COL_3 by default). The
// label arrives already composed (noteBoxes § decorate): the investor's word, its `sensitive`/`system`
// mark and the line under it.
// TEMP (TL, 2026-09-10): validation switched OFF so the operator can walk every step/screen without
// filling required fields. Flip to `false` to restore the rules (backend validation still applies).
export const TEMP_SKIP_VALIDATION = false

export function Field({ span = COL_3, children, rules, ...item }) {
  const liveRules = TEMP_SKIP_VALIDATION ? undefined : rules
  return (
    <Col {...span}>
      <Form.Item {...item} rules={liveRules}>
        {children}
      </Form.Item>
    </Col>
  );
}

// The single Save row, stuck to the bottom of the scroll area (the page wrappers must not clip it:
// the pages set `overflow: visible` on `.web-chat__main-content` and `.creating` for that).
export function SaveBar({ children }) {
  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        background: 'rgba(255, 255, 255, 0.96)',
        borderTop: '1px solid #f0f0f0',
        padding: '12px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {children}
    </div>
  );
}
